import {
  CheckoutAppSelector,
  ConsumerLendingState,
  useAppSelector,
  useCashAppPay,
  useCheckoutState,
  useCohort,
  useConsumerLendingEligible,
  useHasUserInitiatedChangedPaymentMethod,
  useOfferDetailPBI,
  usePreferredBankAccount,
  usePreferredCard,
  usePreferredPaymentMethod,
  useShouldSetApplePayAsPurchaseMethod,
  useShouldShowCashAppTooltip,
  usePreAssessmentMutation,
  useCreateFacilityMutation,
  useCreateFacilityAssessmentMutation,
  useAppRouter,
  useCreateBankingSessionMutation,
  useValidateFacilityIncomeMutation,
  useConsumerSelector,
  useCountry,
  usePaymentTypeAllowInConvergence,
} from '..'
import { createSelector } from '@reduxjs/toolkit'
import { Features, PaymentMethodType, PaymentOption, PaymentOptionsError, PaymentOptionsStatus, SelectedPaymentMethod, TerminalErrorCode } from '../../model'
import { Country, IdentityCheckStatus, PaymentType } from '@afterpay/types'
import { AssessmentRequest, CLRiskDeclineError, FacilityContext, RatesConfig } from './types'
import { useFlag } from '@afterpaytouch/feature'
import { usePercent } from '@afterpaytouch/core'
import { Cohort, ErrorCode, isFetchBaseQueryError, PortalApiError } from '@afterpaytouch/portal-api'
import { useCallback } from 'react'

/* Selectors */

export const consumerLendingSelector: CheckoutAppSelector<ConsumerLendingState> = (state) => state.consumerLending

export const selectedPaymentTypeSelector: CheckoutAppSelector<PaymentType | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.selectedPaymentType
)

export const paymentOptionsStatusSelector: CheckoutAppSelector<PaymentOptionsStatus> = createSelector(
  consumerLendingSelector,
  (state) => state.paymentOptionsStatus
)

export const selectedPaymentOptionSelector: CheckoutAppSelector<PaymentOption | undefined> = createSelector(consumerLendingSelector, (state) =>
  state.paymentOptions.find((paymentOption) => state.selectedPaymentOptionId === paymentOption.id)
)

export const selectedAutopayEnabledSelector: CheckoutAppSelector<boolean | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.isAutopayEnabled
)

export const paymentOptionsSelector: CheckoutAppSelector<PaymentOption[]> = createSelector(consumerLendingSelector, (state) => state.paymentOptions)

export const pclPaymentOptionsSelector: CheckoutAppSelector<PaymentOption[]> = createSelector(consumerLendingSelector, (state) =>
  state.paymentOptions.filter((paymentOption) => paymentOption.paymentType === PaymentType.PCL)
)

export const pbiPaymentOptionSelector: CheckoutAppSelector<PaymentOption | undefined> = createSelector(consumerLendingSelector, (state) =>
  state.paymentOptions.find((paymentOption) => paymentOption.paymentType === PaymentType.PBI)
)

export const paymentOptionsErrorSelector: CheckoutAppSelector<PaymentOptionsError | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state?.error
)

export const selectedLoanAgreementIdSelector: CheckoutAppSelector<string | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.loanAgreementDocumentId
)

export const selectedPaymentAuthorisationIdSelector: CheckoutAppSelector<string | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.paymentAuthorisationDocumentId
)

export const creditScoreDisclosureIdSelector: CheckoutAppSelector<string | undefined> = createSelector(
  consumerLendingSelector,
  (state) => state.creditScoreDisclosureDocumentId
)

export const showPaymentOptionsErrorModalSelector: CheckoutAppSelector<boolean> = createSelector(
  consumerLendingSelector,
  (state) => state.showPaymentOptionsErrorModal
)

export const showEfaModalSelector: CheckoutAppSelector<boolean> = createSelector(consumerLendingSelector, (state) => state.showEfaModal)

export const showBankingServiceUnavailableErrorSelector: CheckoutAppSelector<boolean> = createSelector(
  consumerLendingSelector,
  (state) => state.showBankingServiceUnavailableError
)

export const assessmentRequestSelector: CheckoutAppSelector<AssessmentRequest> = createSelector(consumerLendingSelector, (state) => state.assessmentRequest)

/* React Hooks */

export const useConsumerLendingState = (): ConsumerLendingState => useAppSelector(consumerLendingSelector)

export const useSelectedPaymentType = (): PaymentType | undefined => useAppSelector(selectedPaymentTypeSelector)

export const useSelectedPaymentOption = (): PaymentOption | undefined => useAppSelector(selectedPaymentOptionSelector)

export const useIsConsumerLendingOrder = (): boolean => {
  const selectedPaymentOption = useSelectedPaymentOption()
  return typeof selectedPaymentOption !== 'undefined' && PaymentType.PCL === selectedPaymentOption.paymentType
}

export const usePaymentOptionsStatus = (): PaymentOptionsStatus => useAppSelector(paymentOptionsStatusSelector)

// @ts-expect-error: OPERATION BLEED STOPPER
export const useIsConsumerLendingAutopayEnabled = (): boolean => useAppSelector(selectedAutopayEnabledSelector)

export const usePaymentOptions = (): PaymentOption[] => useAppSelector(paymentOptionsSelector)

export const usePclPaymentOptions = (): PaymentOption[] => useAppSelector(pclPaymentOptionsSelector)

// @ts-expect-error: OPERATION BLEED STOPPER
export const usePbiPaymentOption = (): PaymentOption => useAppSelector(pbiPaymentOptionSelector)

export const usePaymentOptionsError = (): PaymentOptionsError | undefined => useAppSelector(paymentOptionsErrorSelector)

export const useConsumerLendingStartDate = (): string => {
  const { started } = useCheckoutState()
  const paymentOption = useSelectedPaymentOption()
  // @ts-expect-error: OPERATION BLEED STOPPER
  const hasDownPayment = parseFloat(paymentOption.amountDueToday.amount) > 0
  // @ts-expect-error: OPERATION BLEED STOPPER
  return started.paymentSchedules[hasDownPayment ? 1 : 0].installmentDueDate
}
// @ts-expect-error: OPERATION BLEED STOPPER
export const useLoanAgreementDocumentId = (): string => useAppSelector(selectedLoanAgreementIdSelector)
// @ts-expect-error: OPERATION BLEED STOPPER
export const usePaymentAuthorisationDocumentId = (): string => useAppSelector(selectedPaymentAuthorisationIdSelector)
// @ts-expect-error: OPERATION BLEED STOPPER
export const useCreditScoreDisclosureDocumentId = (): string => useAppSelector(creditScoreDisclosureIdSelector)

export const useShowPaymentOptionsErrorModal = (): boolean => useAppSelector(showPaymentOptionsErrorModalSelector)

export const useShowEfaModal = (): boolean => useAppSelector(showEfaModalSelector)

export const useShowBankingServiceUnavailableError = (): boolean => useAppSelector(showBankingServiceUnavailableErrorSelector)

export const useAssessmentRequest = (): AssessmentRequest => useAppSelector(assessmentRequestSelector)

export const usePaymentOptionsHasPCL = (): boolean => {
  const paymentOptionsStatus = usePaymentOptionsStatus()
  const paymentOptions = usePaymentOptions()

  return paymentOptionsStatus === PaymentOptionsStatus.Fetched && paymentOptions.some((po) => po.paymentType === PaymentType.PCL)
}

export const useShowSummaryChangeButton = (): boolean => {
  const paymentOptionsError = usePaymentOptionsError()
  const paymentOptionsStatus = usePaymentOptionsStatus()
  const paymentOptions = usePaymentOptions()
  const isConsumerLendingEligible = useConsumerLendingEligible()
  const { isError: isCLRiskError } = useCLRiskDeclineError()
  const [_, { isError: isCreateBankingSessionError }] = useCreateBankingSessionMutation({ fixedCacheKey: 'create-banking-session-cache' })
  const showBankingServiceUnavailableError = useShowBankingServiceUnavailableError()
  const isConsumerLendingAllowInConvergence = usePaymentTypeAllowInConvergence(PaymentType.PCL)

  return (
    // show button if user is eligible for consumer lending
    isConsumerLendingEligible &&
    // and if payment-options have been fetched and there are no PCL payment options
    // otherwise, skip this check if payment options have not yet been fetched
    ((paymentOptionsStatus === PaymentOptionsStatus.Fetched && paymentOptions.some((po) => po.paymentType === PaymentType.PCL)) ||
      paymentOptionsStatus !== PaymentOptionsStatus.Fetched) &&
    // and if user hasn't hit max attempts on the IdentityVerification form
    typeof paymentOptionsError === 'undefined' &&
    // and if user was not already declined for pay monthly
    !isCLRiskError &&
    // is no create banking session errors
    !isCreateBankingSessionError &&
    !showBankingServiceUnavailableError &&
    isConsumerLendingAllowInConvergence
  )
}

export const usePBIRejected = (): boolean => {
  const offerDetailPBI = useOfferDetailPBI()
  return Boolean(offerDetailPBI?.rejectedReasonCode)
}

export const useAppropriatePaymentMethod = (): SelectedPaymentMethod | null => {
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const preferredBankAccount = usePreferredBankAccount()
  const preferredCard = usePreferredCard()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const cashAppPay = useCashAppPay()
  const { shouldOverrideCashAppPay } = useShouldShowCashAppTooltip()
  const selectedPaymentOption = useSelectedPaymentOption()
  const userInitiatedChangedPaymentMethod = useHasUserInitiatedChangedPaymentMethod()

  const account = preferredBankAccount?.account ?? null
  const card = preferredCard?.card ?? null
  const isConsumerLending = typeof selectedPaymentOption !== 'undefined' && selectedPaymentOption.paymentType === PaymentType.PCL

  if (userInitiatedChangedPaymentMethod) {
    // if user has manually changed payment method
    // should only be triggered from PaymentOptionsModal flow
    return null
  }

  // card should have higher priority than other payment methods
  // use preferred card for PCL orders if they have another blocked payment method, even card is invalid
  if (cashAppPay.length > 0 && (preferredPaymentMethod === PaymentMethodType.CASH_APP_PAY || shouldOverrideCashAppPay)) {
    if (isConsumerLending && card !== null) {
      return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
    }
    return { type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }
  } else if (shouldUseApplePay) {
    if (isConsumerLending && card !== null) {
      return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
    }
    return { type: PaymentMethodType.APPLE_PAY }
  } else if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && isConsumerLending && card !== null) {
    // use preferred card for PCL orders if they have a card instead of ach bank account.
    // shouldn't happen on PaymentTypes page since selectedPaymentOption would be undefined at this stage
    return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
  } else if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && account !== null) {
    return { type: PaymentMethodType.ACH_BANK_ACCOUNT, account }
  } else if (preferredPaymentMethod === PaymentMethodType.CREDIT_CARD && card !== null) {
    return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
  } else if (cashAppPay.length > 0) {
    // fallback to cashapp
    return { type: PaymentMethodType.CASH_APP_PAY, cashAppPay: cashAppPay[0] }
  } else if (card !== null) {
    // fallback to card
    return { type: PaymentMethodType.CREDIT_CARD, card: { token: card.token } }
  } else {
    // handle the no eligible payment method scenario by returning null
    return null
  }
}

export const useGetErrorContext = (): FacilityContext | undefined => {
  const [, { isError: isFacilityCreationError }] = useCreateFacilityMutation({ fixedCacheKey: 'create-facility-cache' })
  const [, { isError: isPreAssessmentError }] = usePreAssessmentMutation({
    fixedCacheKey: 'shared-pre-assessment',
  })
  const [, { isError: isFacilityAssessmentError }] = useCreateFacilityAssessmentMutation({
    fixedCacheKey: 'shared-facility-assessment',
  })
  const [, { isError: isIncomeAssessmentError }] = useValidateFacilityIncomeMutation({ fixedCacheKey: 'income-assessment-cache' })
  const paymentOptionsError = usePaymentOptionsError()
  if (isPreAssessmentError) {
    return FacilityContext.PreAssessment
  }
  if (isFacilityAssessmentError) {
    return FacilityContext.Assessment
  }
  if (isFacilityCreationError || paymentOptionsError != null) {
    return FacilityContext.FacilityCreation
  }
  if (isIncomeAssessmentError) {
    return FacilityContext.IncomeAssessment
  }
  return undefined
}

export const useCLRiskDeclineError = (): CLRiskDeclineError => {
  const [, { reset: facilityCreationReset }] = useCreateFacilityMutation({ fixedCacheKey: 'create-facility-cache' })
  const [, { isError: isPreAssessmentError, error: preAssessmentError, reset: preAssessmentReset }] = usePreAssessmentMutation({
    fixedCacheKey: 'shared-pre-assessment',
  })
  const [, { isError: isFacilityAssessmentError, error: facilityAssessmentError, reset: facilityAssessmentReset }] = useCreateFacilityAssessmentMutation({
    fixedCacheKey: 'shared-facility-assessment',
  })
  const [, { isError: isIncomeAssessmentError, error: incomeAssessmentError, reset: incomeAssessmentReset }] = useValidateFacilityIncomeMutation({
    fixedCacheKey: 'income-assessment-cache',
  })
  const paymentOptionsError = usePaymentOptionsError()
  const context = useGetErrorContext()
  const isPaymentOptionsError = typeof paymentOptionsError !== 'undefined'

  const getError = (): PortalApiError | PaymentOptionsError | undefined => {
    const pclError = preAssessmentError ?? facilityAssessmentError ?? incomeAssessmentError
    if (isFetchBaseQueryError(pclError)) {
      return pclError?.data
    } else {
      return paymentOptionsError
    }
  }
  const resetCache = useCallback(() => {
    facilityAssessmentReset()
    preAssessmentReset()
    facilityCreationReset()
    incomeAssessmentReset()
  }, [facilityAssessmentReset, preAssessmentReset, facilityCreationReset, incomeAssessmentReset])

  return {
    isError: isPreAssessmentError || isFacilityAssessmentError || isPaymentOptionsError || isIncomeAssessmentError,
    error: getError(),
    context,
    reset: resetCache,
  }
}

export const useRatesConfig = (): RatesConfig => {
  const { locale } = useAppRouter()
  const { formatPercent } = usePercent({ locale, decimalPlaces: 2, shouldDivideBy100: true })
  const ratesConfig = useFlag<RatesConfig>(Features.CONSUMER_LENDING_ADDITIONAL_RATES_CONFIG, {
    maxComparisonRate: '23.42',
    minComparisonRate: '4.23',
    maxInterestRate: '21.99',
  })

  return {
    maxComparisonRate: formatPercent(ratesConfig.maxComparisonRate),
    minComparisonRate: formatPercent(ratesConfig.minComparisonRate),
    maxInterestRate: formatPercent(ratesConfig.maxInterestRate),
  }
}

export const useShowIdvModal = (): boolean => {
  const bypassIdvFlag = useFlag(Features.BYPASS_IDV_FOR_CONSUMER_LENDING_ORDERS)
  const { identityCheckStatuses, shadowAccountExists } = useConsumerSelector()
  const cohort = useCohort()

  // Bypass IDV for:
  // 1. Environments where we need to bypass IDV (e.g. Sandbox) - should be disabled in prod
  // 2. Shadow account customers (CashOnly cohort) who perform IDV externally
  const canBypassIDVForConsumerLendingOrders = bypassIdvFlag || (cohort === Cohort.CashOnly && shadowAccountExists === true)

  return (
    !canBypassIDVForConsumerLendingOrders &&
    !(identityCheckStatuses?.PCL === IdentityCheckStatus.VERIFIED || identityCheckStatuses?.PCL === IdentityCheckStatus.NOT_REQUIRED)
  )
}

export const useIsPayMonthlyOnly = (): boolean => {
  const offerDetailPBI = useOfferDetailPBI()
  const country = useCountry()

  return offerDetailPBI?.rejectedReasonCode === ErrorCode.MerchantPayMonthlyOnly && country === Country.US
}

export const useIsExtendedFraudAlert = (): boolean => {
  const paymentOptionsError = usePaymentOptionsError()
  const country = useCountry()

  return paymentOptionsError?.errorCode === ErrorCode.ExtendedFraudAlert && country === Country.US
}

export const useIsCreditFreezeDeclineFlow = (): boolean => {
  const paymentOptionsError = usePaymentOptionsError()
  const country = useCountry()

  return (
    paymentOptionsError?.error === TerminalErrorCode.CreditFreezeDeclinedPCL &&
    country === Country.US &&
    paymentOptionsError?.extra?.creditFreezeApplicationDeadline !== undefined
  )
}

export const usePreselectedPaymentOption = (): PaymentOption | undefined => {
  const paymentOptions = usePaymentOptions()

  if (paymentOptions.find((po) => po.rank) == null) {
    return undefined
  }

  return [...paymentOptions].sort((a, b) => (a?.rank ?? Infinity) - (b?.rank ?? Infinity))[0]
}
